/**
 * Patched version of @radix-ui/react-compose-refs that is compatible with
 * React 19's ref-callback cleanup semantics.
 *
 * In React 19, when a ref callback changes between renders, React:
 *   1. Calls the OLD callback's cleanup function (if it returned one), OR
 *      calls the old callback with `null` if no cleanup was returned.
 *   2. Calls the NEW callback with the DOM node.
 *
 * The upstream compose-refs (1.1.2) passes unstable inline refs (e.g.
 * `(node) => setState(node)`) as dependencies to `useCallback`, producing
 * a new composed callback every render.  React 19 then runs cleanup →
 * setState(null) → re-render → new callback → cleanup → infinite loop.
 *
 * The fix: store the latest refs in a `useRef` and return a **stable**
 * callback (empty deps) that always reads from the ref.  Because the
 * callback identity never changes, React never triggers the cleanup /
 * re-apply cycle.
 */
import * as React from 'react';

type PossibleRef<T> = React.Ref<T> | undefined;

function setRef<T>(ref: PossibleRef<T>, value: T) {
  if (typeof ref === 'function') {
    return ref(value);
  } else if (ref !== null && ref !== undefined) {
    (ref as React.RefObject<T>).current = value;
  }
}

function composeRefs<T>(...refs: PossibleRef<T>[]) {
  return (node: T) => {
    const cleanups = refs.map((ref) => setRef(ref, node));

    return () => {
      for (let i = 0; i < cleanups.length; i++) {
        const cleanup = cleanups[i];
        if (typeof cleanup === 'function') {
          cleanup();
        } else {
          setRef(refs[i], null as unknown as T);
        }
      }
    };
  };
}

function useComposedRefs<T>(...refs: PossibleRef<T>[]) {
  const refsRef = React.useRef<PossibleRef<T>[]>(refs);

  // Sync the latest refs before React commits the DOM — useInsertionEffect
  // runs synchronously before layout effects (and before ref callbacks), so
  // refsRef.current is always up-to-date when the stable callback fires.
  React.useInsertionEffect(() => { refsRef.current = refs; });

  return React.useCallback((node: T) => {
    const currentRefs = refsRef.current;
    const cleanups = currentRefs.map((ref) => setRef(ref, node));

    return () => {
      for (let i = 0; i < cleanups.length; i++) {
        const cleanup = cleanups[i];
        if (typeof cleanup === 'function') {
          cleanup();
        } else {
          setRef(currentRefs[i], null as unknown as T);
        }
      }
    };
  }, []);
}

export { composeRefs, useComposedRefs };
